import React, { useContext, useEffect, useRef, useState } from "react"
import {
    ActivityIndicator,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { useDispatch, useSelector } from "react-redux"
import Animated, { FadeIn } from "react-native-reanimated"
import { LinearGradient } from "expo-linear-gradient"
import { FontAwesome5 } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import ChampionBubble from "../../../student/champions/ChampionBubble"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import useThrottledSetAttributes from "../../../engine/live/room/useThrottledSetAttributes"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import useOwnApiAnswer from "@common/hooks/useOwnApiAnswer"
import { hasFeature } from "@data/utility/Utils"
import { activitiesService } from "@data/services/activitiesService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { apiRequest } from "@data/redux/actions/api"
import {
    addAutoModeProposal,
    closeAutoMode,
    setAutoModeError,
    setAutoModeLoading,
} from "@data/redux/actions/liveMode"
import AutoModeCloseButton from "./AutoModeCloseButton"
import useAutoModeReplaySame from "@common/hooks/useAutoModeReplaySame"

// Per-intent presentation: Sam-voice label, FA5 icon, accent color. The
// auto-mode picker hero-displays this triplet on each card (option A from
// the Mobbin exploration); the AI-generated material title is hidden because
// for same-difficulty/more-difficult variations the AI returns near-identical
// titles, which made the previous card layout impossible to differentiate.
const INTENT_PRESENTATION = {
    more_difficult: {
        label: "Un défi plus dur",
        icon: "bullseye",
        color: theme.colors.primary,
    },
    other_concepts: {
        label: "Une autre notion de la leçon",
        icon: "random",
        color: theme.colors.purple,
    },
    same_diversified: {
        label: "Un exercice différent, même niveau",
        icon: "magic",
        color: theme.colors.accent,
    },
    same_difficulty: {
        label: "Un exercice du même genre",
        icon: "redo",
        color: theme.colors.warning,
    },
    concept_review: {
        label: "Revoir la leçon",
        icon: "brain",
        color: theme.colors.primary,
    },
    method_review: {
        label: "Revoir la méthode",
        icon: "drafting-compass",
        color: theme.colors.purple,
    },
}

// Map the student's self-eval to a trio of services with *distinct* intents.
// We deliberately diverge from the original spec (which proposed × 3 of the
// same service for got_it/mixed): student feedback showed that 3 cards with
// the same intent are indistinguishable, so each mode now mixes 3 services
// to give 3 genuinely different paths.
//  - got_it → push level + new concept + same-level variant (safety net)
//  - mixed  → same-exo variant + same-level diversified + other concept
//  - lost   → concept review + method review (2 cards; the first-sub-exercise
//             variant was dropped — generation quality wasn't good enough)
const buildRequests = (selectedEval, sourceActivity) => {
    const materialId = sourceActivity?.material?.id
    if (!materialId) return []

    const batchId = `${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 10)}`
    const body = { material_id: materialId, request_batch_id: batchId }

    if (selectedEval === "got_it") {
        return [
            {
                run: () =>
                    activitiesService.generateSimilarExercisesMoreDifficult(
                        {},
                        body
                    ),
                intent: "more_difficult",
            },
            {
                run: () =>
                    activitiesService.generateSimilarExercisesOtherConcepts(
                        {},
                        body
                    ),
                intent: "other_concepts",
            },
            {
                run: () =>
                    activitiesService.generateSimilarExercisesSameDifficultyDiversified(
                        {},
                        body
                    ),
                intent: "same_diversified",
            },
        ]
    }

    if (selectedEval === "mixed") {
        return [
            {
                run: () =>
                    activitiesService.generateSimilarExercisesSameDifficulty(
                        {},
                        body
                    ),
                intent: "same_difficulty",
            },
            {
                run: () =>
                    activitiesService.generateSimilarExercisesSameDifficultyDiversified(
                        {},
                        body
                    ),
                intent: "same_diversified",
            },
            {
                run: () =>
                    activitiesService.generateSimilarExercisesOtherConcepts(
                        {},
                        body
                    ),
                intent: "other_concepts",
            },
        ]
    }

    if (selectedEval === "lost") {
        // `order` is required by the backend validator (MaterialController
        // generateProposition) and is also written onto the resulting Material
        // — passing 1/2 gives us 2 distinct sub-materials per batch.
        const bodyLost = (order) => ({
            num_clusters: 1,
            order,
            request_batch_id: batchId,
        })
        return [
            {
                run: () =>
                    activitiesService.generatePropositionConceptReview(
                        { materialId },
                        bodyLost(1)
                    ),
                intent: "concept_review",
            },
            {
                run: () =>
                    activitiesService.generatePropositionMethodReview(
                        { materialId },
                        bodyLost(2)
                    ),
                intent: "method_review",
            },
        ]
    }

    return []
}

const ProposalCard = ({ material, onChoose, disabled, isChosen }) => {
    const presentation = INTENT_PRESENTATION[material?._autoModeIntent]
    const subject = material?.school_subject?.name
    const durationLabel = material?.estimated_duration
        ? `${material.estimated_duration} min`
        : null
    const levelLabel = material?.level ? `Niveau ${material.level}` : null
    const accent = presentation?.color || theme.colors.primary

    return (
        <TouchableOpacity
            disabled={disabled}
            onPress={() => onChoose(material)}
            style={[
                styles.proposalCard,
                disabled && !isChosen && { opacity: 0.45 },
            ]}
            activeOpacity={0.85}
        >
            <View style={styles.cardRow}>
                <View
                    style={[
                        styles.iconBubble,
                        { backgroundColor: `${accent}1A` },
                    ]}
                >
                    <FontAwesome5
                        name={presentation?.icon || "play"}
                        size={22}
                        color={accent}
                    />
                </View>
                <View style={styles.cardBody}>
                    <TextM
                        fontSize={16}
                        fontWeight="ExtraBold"
                        color={theme.colors.primary}
                        wrap
                        numberOfLines={2}
                    >
                        {presentation?.label || "Activité"}
                    </TextM>
                    <View style={styles.metaRow}>
                        {subject ? (
                            <TextM
                                fontSize={12}
                                fontWeight="Bold"
                                color={accent}
                                style={{ marginRight: 10 }}
                            >
                                {subject}
                            </TextM>
                        ) : null}
                        {levelLabel ? (
                            <TextM
                                fontSize={12}
                                fontWeight="SemiBold"
                                color={theme.colors.grey}
                                style={{ marginRight: 10 }}
                            >
                                {levelLabel}
                            </TextM>
                        ) : null}
                        {durationLabel ? (
                            <TextM
                                fontSize={12}
                                fontWeight="SemiBold"
                                color={theme.colors.grey}
                            >
                                ⏱ {durationLabel}
                            </TextM>
                        ) : null}
                    </View>
                </View>
                <FontAwesome5
                    name="chevron-right"
                    size={16}
                    color={theme.colors.lightMore}
                />
            </View>
            {isChosen ? (
                <View style={styles.chosenOverlay}>
                    <ActivityIndicator
                        color={theme.colors.primary}
                        size="large"
                    />
                </View>
            ) : null}
        </TouchableOpacity>
    )
}

// « Refaire la même activité » : une option de plein droit, au même niveau que
// les propositions IA — même carte, même gabarit. Elle ferme la liste parce
// qu'elle est le repli, pas parce qu'elle vaut moins.
//
// Elle reste affichée même quand elle est indisponible (support absent) :
// une entrée qui apparaît sur une activité et disparaît sur la suivante se lit
// comme un bug. Dans ce cas la carte est atténuée et porte sa raison.
const ReplayCard = ({ sourceActivity, onPress, disabled, dimmed, isLoading, reason, error }) => {
    const material = sourceActivity?.material
    const subject = material?.school_subject?.name
    const durationLabel = material?.estimated_duration
        ? `${material.estimated_duration} min`
        : null
    const accent = theme.colors.primary

    return (
        <>
            <TouchableOpacity
                disabled={disabled}
                onPress={onPress}
                style={[styles.proposalCard, dimmed && { opacity: 0.45 }]}
                activeOpacity={0.85}
            >
                <View style={styles.cardRow}>
                    <View
                        style={[
                            styles.iconBubble,
                            { backgroundColor: `${accent}1A` },
                        ]}
                    >
                        <FontAwesome5 name="redo" size={22} color={accent} />
                    </View>
                    <View style={styles.cardBody}>
                        <TextM
                            fontSize={16}
                            fontWeight="ExtraBold"
                            color={theme.colors.primary}
                            wrap
                            numberOfLines={2}
                        >
                            Refaire la même activité
                        </TextM>
                        <View style={styles.metaRow}>
                            {reason ? (
                                <TextM
                                    fontSize={12}
                                    fontWeight="SemiBold"
                                    color={theme.colors.grey}
                                    wrap
                                >
                                    {reason}
                                </TextM>
                            ) : (
                                <>
                                    {subject ? (
                                        <TextM
                                            fontSize={12}
                                            fontWeight="Bold"
                                            color={accent}
                                            style={{ marginRight: 10 }}
                                        >
                                            {subject}
                                        </TextM>
                                    ) : null}
                                    {durationLabel ? (
                                        <TextM
                                            fontSize={12}
                                            fontWeight="SemiBold"
                                            color={theme.colors.grey}
                                        >
                                            ⏱ {durationLabel}
                                        </TextM>
                                    ) : null}
                                </>
                            )}
                        </View>
                    </View>
                    <FontAwesome5
                        name="chevron-right"
                        size={16}
                        color={theme.colors.lightMore}
                    />
                </View>
                {isLoading ? (
                    <View style={styles.chosenOverlay}>
                        <ActivityIndicator
                            color={theme.colors.primary}
                            size="large"
                        />
                    </View>
                ) : null}
            </TouchableOpacity>
            {error ? (
                <TextM
                    fontSize={12}
                    color={theme.colors.error}
                    style={{ textAlign: "center", marginTop: 4 }}
                    wrap
                >
                    {error}
                </TextM>
            ) : null}
        </>
    )
}

const AutoModeChoiceModal = () => {
    const dispatch = useDispatch()
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const launchActivity = useLaunchActivity()
    const visible = useSelector((s) => s.liveMode?.autoMode?.showChoiceModal)
    const selectedEval = useSelector(
        (s) => s.liveMode?.autoMode?.selectedEval
    )
    const sourceActivity = useSelector(
        (s) => s.liveMode?.autoMode?.sourceActivity
    )
    const proposals = useSelector(
        (s) => s.liveMode?.autoMode?.proposals ?? []
    )
    const isLoading = useSelector((s) => s.liveMode?.autoMode?.isLoading)
    const error = useSelector((s) => s.liveMode?.autoMode?.error)
    const isLiveModeEnabled = useSelector(
        (s) => s.liveMode?.isLiveModeEnabled
    )
    const liveSessionId = useSelector((s) => s.liveMode?.liveSessionId)
    const currentLiveSession = useSelector((s) =>
        s.data?.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === s.liveMode?.liveSessionId
        )
    )
    const currentLiveRoom = currentLiveSession?.live_room
    const userDisplayName = useSelector(
        (s) => s.data?.currentUser?.object?.display_name
    )
    const isPrerequisiteQuizEnabled = hasFeature(
        currentLiveRoom,
        "prerequisite-quiz"
    )
    const setAttributesThrottled = useThrottledSetAttributes()
    const { sendLiveKitData } = useLiveKitEventSender()
    const {
        replaySame,
        isLoading: isReplayLoading,
        error: replayError,
        canReplay,
        unavailableReason: replayUnavailableReason,
    } = useAutoModeReplaySame()

    // How many proposals this eval fans out (keep in sync with buildRequests):
    // lost shows 2 (concept + method review), got_it/mixed show 3. Drives the
    // loading-skeleton count so we never render a skeleton with no card behind it.
    const expectedProposalCount = selectedEval === "lost" ? 2 : 3

    const [chosenMaterialId, setChosenMaterialId] = useState(null)
    const [chooseError, setChooseError] = useState(null)
    const dispatchedRef = useRef(false)

    const handleRaiseHand = () => {
        if (liveSessionId) {
            setAttributesThrottled({
                raise_hand_source: "",
                display_name: userDisplayName,
                skip_checkpoint: "1",
            }).catch(() => {})
            dispatch(
                apiRequest(
                    liveServiceV2.raiseHandV2,
                    { id: liveSessionId },
                    { raised: true }
                )
            )
        }
        dispatch(closeAutoMode())
    }

    // Fan out the 3 generation calls when the choice modal opens. Using raw
    // axios via the service functions (not apiRequest/useApiStatus) because
    // got_it/mixed call the same service 3 times — useApiStatus only tracks
    // one success transition per service, so we'd lose 2 of the 3 responses.
    useEffect(() => {
        if (!visible || !selectedEval || dispatchedRef.current) return
        const requests = buildRequests(selectedEval, sourceActivity)
        if (!requests.length) {
            dispatch(
                setAutoModeError(
                    "Impossible de générer des suggestions pour cette activité."
                )
            )
            return
        }

        dispatchedRef.current = true
        dispatch(setAutoModeLoading(true))

        ;(async () => {
            const results = await Promise.allSettled(
                requests.map((r) => r.run())
            )

            let successCount = 0
            results.forEach((result, idx) => {
                if (result.status === "fulfilled") {
                    // axios responses come from Laravel JsonResource → response.data.data
                    const material =
                        result.value?.data?.data ?? result.value?.data ?? null
                    if (material?.id) {
                        // Tag the material with its source intent so the card
                        // can render Sam's "what this activity is for" line.
                        dispatch(
                            addAutoModeProposal({
                                ...material,
                                _autoModeIntent: requests[idx].intent,
                            })
                        )
                        successCount += 1
                    }
                }
            })

            dispatch(setAutoModeLoading(false))
            if (successCount === 0) {
                dispatch(
                    setAutoModeError(
                        "Désolé, je n'arrive pas à te proposer une activité."
                    )
                )
            }
        })()
    }, [visible, selectedEval])

    // Reset latch when the modal closes
    useEffect(() => {
        if (!visible) {
            dispatchedRef.current = false
            setChosenMaterialId(null)
            setChooseError(null)
        }
    }, [visible])

    // Play a soft Sam jingle when the cards arrive (after the loading spinners).
    useEffect(() => {
        if (visible && !isLoading && proposals.length > 0) {
            PlaySoundM.play(
                require("../../../../assets/sounds/confirmation_002.mp3")
            )
        }
    }, [visible, isLoading, proposals.length])

    // Launch + tutor-notification side effects for a chosen proposal. The
    // replay flow (useAutoModeReplaySame) and other screens of the student
    // drive the same slice: useOwnApiAnswer keeps these effects to an actual
    // proposal choice.
    const sendChoice = useOwnApiAnswer(
        activitiesService.activityTodoAsStudent,
        (activity) => {
            if (!activity?.id) {
                setChosenMaterialId(null)
                notifyBugsnag(
                    new Error("[autoMode] choose: empty activity response")
                )
                setChooseError(
                    "Impossible de lancer cette activité. Réessaie."
                )
                return
            }
            // Surface the autonomous choice to the tutor's events panel: a
            // single event carrying both the self-eval and the chosen activity
            // (the card's "Voir l'énoncé" CTA opens it). Sent only to the
            // session's tutors so a peer never receives another's self-eval.
            const tutorIds = (currentLiveSession?.users ?? [])
                .filter((u) => u?.is_tutor)
                .map((u) => u?.id)
            if (tutorIds.length > 0) {
                sendLiveKitData(
                    "autoModeActivityStarted",
                    { evaluation: selectedEval, activityId: activity.id },
                    tutorIds
                )
            }
            dispatch(closeAutoMode())
            launchActivity(activity, "replace")
            PlaySoundM.play(require("../../../../assets/sounds/falling.mp3"))
            SnackBarM.show({
                text1: "🎯 Nouvelle activité lancée",
                duration: 3000,
            })
        },
        (err) => {
            setChosenMaterialId(null)
            // Offline is already surfaced by apiRequest's own retry dialog.
            if (err?.isOffline) return
            notifyBugsnag(err)
            const status = err?.status
            const backendMsg = err?.message
            const errBits = [status, backendMsg].filter(Boolean).join(" — ")
            setChooseError(
                errBits
                    ? `Impossible de lancer cette activité (${errBits}). Réessaie.`
                    : "Impossible de lancer cette activité. Réessaie."
            )
        }
    )

    const handleChoose = (material) => {
        if (chosenMaterialId || isReplayLoading) return
        setChooseError(null)
        setChosenMaterialId(material?.id ?? "pending")

        // Mirror the tutor default in MaterialResultsCards: quiz ON for levels
        // 1 ("Entraînement") and 2 ("Révision du cours"), OFF for level 3
        // ("Approfondissement"). Gated on the live-room feature flag so we
        // don't request a quiz the backend can't build. Coerce to number — the
        // tutor mirror compares as string ("1"/"2") because its source is a
        // form picker, whereas here `material.level` comes from the API.
        const materialLevel = Number(material?.level)
        const hasPrerequisiteQuiz =
            isPrerequisiteQuizEnabled &&
            (materialLevel === 1 || materialLevel === 2)

        // Student-scoped endpoint (POST /api/v2/activities/todo). The
        // tutor-scoped equivalent (createActivityAsTutor) requires the
        // `manageActivitiesForStudent` ability, which the student role does not
        // have. Body kept to material_id (+ quiz flag) so the backend hits the
        // fast path (ActivityController::newTodoActivity) with no AI call. Goes
        // through apiRequest so the created activity is synced by the existing
        // activityTodoAsStudent _SUCCESS reducer case — same as the replay flow.
        sendChoice(
            {},
            {
                material_id: material?.id,
                has_prerequisite_quiz: hasPrerequisiteQuiz,
            }
        )
    }

    if (!visible) return null

    const footerSpace = isLiveModeEnabled ? 70 + insets.bottom : insets.bottom
    const isWeb = Platform.OS === "web"
    const isLargeScreen = isWeb && (dimensions?.width || 0) >= 800
    const contentWidth = isLargeScreen
        ? Math.min((dimensions?.width || 0) - 80, 720)
        : (dimensions?.width || 0) - 40

    const renderEmptyState = error && proposals.length === 0

    const headerText = renderEmptyState
        ? error
        : isLoading && proposals.length === 0
        ? "Je te prépare 3 activités. Une seconde…"
        : selectedEval === "lost"
        ? "On reprend les bases ensemble. Choisis ta prochaine activité."
        : selectedEval === "got_it"
        ? "Bravo ! Voici 3 activités plus poussées. Choisis celle qui te tente."
        : "Encore quelques variations pour bien ancrer. Choisis ta prochaine."

    return (
        <Animated.View
            entering={FadeIn.duration(300)}
            style={[
                styles.overlayContainer,
                {
                    width: "100%",
                    height: (dimensions?.height || 0) - footerSpace,
                    top: 0,
                    left: 0,
                    paddingTop: insets.top,
                },
            ]}
        >
            <View style={styles.backdrop} />
            <LinearGradient
                colors={["#F8FAFC", "#F1F5F9", "#E2E8F0"]}
                style={StyleSheet.absoluteFillObject}
            />

            <View style={styles.contentContainer}>
                <ScrollView
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <ChampionBubble
                        name="sam"
                        variant="main"
                        championPosition="left"
                        texts={[headerText]}
                        width={contentWidth}
                        animated={false}
                        isModal={false}
                    />

                    {chooseError ? (
                        <View style={[styles.inlineError, { width: contentWidth }]}>
                            <TextM
                                fontSize={13}
                                color={theme.colors.error}
                                style={{ textAlign: "center" }}
                            >
                                {chooseError}
                            </TextM>
                        </View>
                    ) : null}

                    <View style={[styles.cardsContainer, { width: contentWidth }]}>
                        {renderEmptyState ? (
                            <View style={styles.errorBox}>
                                <FilledButtonM
                                    label="Lever la main"
                                    icon="hand-paper"
                                    color={theme.colors.warning}
                                    onPress={handleRaiseHand}
                                    style={{ width: null }}
                                    wrapText
                                />
                            </View>
                        ) : (
                            <>
                                {proposals.map((p, idx) => (
                                    <View key={p?.id ?? idx}>
                                        <ProposalCard
                                            material={p}
                                            onChoose={handleChoose}
                                            disabled={
                                                !!chosenMaterialId ||
                                                isReplayLoading
                                            }
                                            isChosen={chosenMaterialId === p.id}
                                        />
                                    </View>
                                ))}
                                {isLoading
                                    ? Array.from({
                                          length: Math.max(
                                              0,
                                              expectedProposalCount -
                                                  proposals.length
                                          ),
                                      }).map((_, idx) => (
                                          <View
                                              key={`skeleton-${idx}`}
                                              style={styles.skeletonCard}
                                          >
                                              <ActivityIndicator
                                                  color={theme.colors.primary}
                                              />
                                          </View>
                                      ))
                                    : null}
                            </>
                        )}

                        <ReplayCard
                            sourceActivity={sourceActivity}
                            onPress={replaySame}
                            // Spinner pour une vraie relance seulement : un choix
                            // de proposition partage le même slot API mais montre
                            // son propre spinner sur sa carte.
                            isLoading={isReplayLoading && !chosenMaterialId}
                            error={replayError}
                            disabled={
                                !canReplay || isReplayLoading || !!chosenMaterialId
                            }
                            dimmed={!canReplay || !!chosenMaterialId}
                            reason={replayUnavailableReason}
                        />
                    </View>
                </ScrollView>
            </View>

            <AutoModeCloseButton
                visible={visible}
                onPress={() => dispatch(closeAutoMode())}
                disabled={!!chosenMaterialId}
            />
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    overlayContainer: {
        position: "absolute",
        zIndex: 8,
        elevation: 8,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    contentContainer: {
        flex: 1,
        zIndex: 2,
    },
    scrollContent: {
        ...(Platform.OS === "web" ? { flexGrow: 1 } : { flex: 1 }),
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: 20,
        paddingBottom: 80,
        paddingHorizontal: 20,
    },
    cardsContainer: {
        marginTop: 20,
        alignSelf: "center",
    },
    proposalCard: {
        backgroundColor: theme.colors.white,
        borderRadius: 16,
        padding: 16,
        marginVertical: 8,
        shadowColor: theme.colors.grey,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4,
    },
    cardRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
    },
    iconBubble: {
        width: 52,
        height: 52,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    cardBody: {
        flex: 1,
        minWidth: 0,
    },
    metaRow: {
        flexDirection: "row",
        marginTop: 6,
        alignItems: "center",
        flexWrap: "wrap",
    },
    skeletonCard: {
        height: 90,
        borderRadius: 16,
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        marginVertical: 8,
        alignItems: "center",
        justifyContent: "center",
    },
    errorBox: {
        padding: 16,
        alignItems: "center",
    },
    inlineError: {
        marginTop: 12,
        paddingHorizontal: 10,
    },
    chosenOverlay: {
        ...StyleSheet.absoluteFillObject,
        borderRadius: 16,
        backgroundColor: "rgba(255, 255, 255, 0.85)",
        alignItems: "center",
        justifyContent: "center",
    },
})

export default AutoModeChoiceModal
